import type { NotificationItem } from "../types/notifications.types";
import { NotificationType } from "../types/notifications.types";

/**
 * Map an entity-linked notification to an in-app route.
 * Pure function: no routing or auth side-effects. Returns null when the
 * notification has no navigable target.
 *
 * Supported linkage:
 *  - entityType = "education" / "enrollment" / "exam" / "survey" / "certificate" / "ticket"
 *  - OR specific NotificationType where the route is implicit.
 *
 * Falls back to `data.route` (server-supplied hint) when provided.
 */
export function resolveNotificationRoute(
  n: NotificationItem,
): string | null {
  // Explicit server hint wins.
  const rawData = (n.data ?? {}) as Record<string, unknown>;
  if (typeof rawData.route === "string" && rawData.route.startsWith("/")) {
    return rawData.route;
  }

  const type = Number(n.type);
  const id = n.entityId ?? "";

  // Type-driven routing (works even when entityType is missing).
  switch (type) {
    case NotificationType.CERTIFICATE_ISSUED:
      return "/dashboard/client-certificates";
    case NotificationType.EXAM_ELIGIBLE:
    case NotificationType.EXAM_COMPLETED:
      return "/dashboard/client-exams";
    case NotificationType.SURVEY_ELIGIBLE:
    case NotificationType.SURVEY_COMPLETED:
      return "/dashboard/client-polls";
    case NotificationType.EDUCATION_OPENED:
    case NotificationType.EDUCATION_UPCOMING_30D:
    case NotificationType.EDUCATION_UPCOMING_7D:
    case NotificationType.EDUCATION_UPCOMING_1D:
      return "/dashboard/client-educations";
    case NotificationType.ENROLLMENT_REJECTED_FINAL:
      return "/dashboard/client-education-requests";
    case NotificationType.TICKET_REPLIED:
      return "/dashboard/client-support-tickets";
    default:
      break;
  }

  // Entity-driven fallback.
  switch (n.entityType) {
    case "education":
      return id
        ? `/dashboard/client-educations?id=${encodeURIComponent(id)}`
        : "/dashboard/client-educations";
    case "enrollment":
      return "/dashboard/client-education-requests";
    case "exam":
      return "/dashboard/client-exams";
    case "survey":
      return "/dashboard/client-polls";
    case "certificate":
      return "/dashboard/client-certificates";
    case "ticket":
      return "/dashboard/client-support-tickets";
    default:
      return null;
  }
}
